import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatINR, formatDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PaymentBadge, PayStatusBadge, SpotPayBadge } from "@/components/badges";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pencil, Wallet, Printer } from "lucide-react";
import PaymentCollectionModal from "@/components/PaymentCollectionModal";

const MODES = ["Cash", "UPI", "Card", "Cheque"];

export default function Payments() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [view, setView] = useState("pending");
  const [mode, setMode] = useState("all");
  const [area, setArea] = useState("all");
  const [sort, setSort] = useState("date");
  const [order, setOrder] = useState("desc");
  const [editing, setEditing] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [form, setForm] = useState({ mode: "Cash", amount: "", reference: "", bank: "", card_last4: "", remarks: "" });

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ["payments", mode, area, sort, order],
    queryFn: async () => (await api.get("/payments", { params: { mode, area, sort, order } })).data,
  });
  const { data: areas = [] } = useQuery({ queryKey: ["areas"], queryFn: async () => (await api.get("/customers/areas")).data });
  const { data: deliveries = [], isLoading: pendingLoading } = useQuery({
    queryKey: ["deliveries", "pending"],
    queryFn: async () => (await api.get("/deliveries")).data,
  });
  const pendingBills = deliveries
    .filter((d) => d.status !== "Cancelled" && (d.outstanding || 0) > 0.01)
    .sort((a, b) => b.outstanding - a.outstanding);
  const totalDue = pendingBills.reduce((s, d) => s + (d.outstanding || 0), 0);

  const save = useMutation({
    mutationFn: () => api.put(`/payments/${editing}`, { ...form, amount: parseFloat(form.amount) || 0 }),
    onSuccess: () => {
      toast.success("Payment updated");
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["deliveries"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      setEditing(null);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openEdit = (p) => {
    setForm({ mode: p.mode, amount: String(p.amount), reference: p.reference || "", bank: p.bank || "", card_last4: p.card_last4 || "", remarks: p.remarks || "" });
    setEditing(p.id);
  };

  const refDisplay = (p) => {
    if (p.mode === "UPI") return p.reference || "—";
    if (p.mode === "Card") return p.card_last4 ? `•••• ${p.card_last4}` : "—";
    if (p.mode === "Cheque") return [p.reference, p.bank].filter(Boolean).join(" · ") || "—";
    return p.remarks || "—";
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Payments</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {view === "pending"
              ? `${pendingBills.length} pending bills · ${formatINR(totalDue)} due · nil-balance customers hidden`
              : `${payments.length} payments · filter & sort${user?.role === "admin" ? " · admins can edit" : ""}`}
          </p>
        </div>
        {view === "pending" && (
          <Button variant="outline" onClick={() => window.print()} data-testid="pending-bills-print"><Printer className="mr-2 h-4 w-4" /> Print</Button>
        )}
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList data-testid="payments-view-tabs">
          <TabsTrigger value="pending" data-testid="payments-tab-pending">Pending Bills</TabsTrigger>
          <TabsTrigger value="history" data-testid="payments-tab-history">Payment History</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "history" && (
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="payment-mode-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Modes</SelectItem>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger data-testid="payment-area-filter"><SelectValue placeholder="Area" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Areas</SelectItem>{areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="payment-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Sort: Date</SelectItem>
              <SelectItem value="amount">Sort: Amount</SelectItem>
              <SelectItem value="mode">Sort: Payment Type</SelectItem>
              <SelectItem value="area">Sort: Area</SelectItem>
              <SelectItem value="customer">Sort: Customer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={order} onValueChange={setOrder}>
            <SelectTrigger data-testid="payment-order-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="desc">Descending</SelectItem><SelectItem value="asc">Ascending</SelectItem></SelectContent>
          </Select>
        </div>
      </Card>
      )}

      {view === "pending" && (
        <div id="report-print">
        <div className="print-show">
          <h2 className="font-display text-xl font-bold">SARAVANA DELAPP</h2>
          <p className="mb-3 text-sm text-muted-foreground">Pending Bills — Collection Sheet · Generated {new Date().toLocaleString("en-IN")}</p>
        </div>
        <Card className="overflow-hidden" data-testid="pending-bills-card">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60">
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Invoices</TableHead>
                  <TableHead className="text-right">Bill Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance Due</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                  <TableHead className="print-hidden text-right">Collect</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingLoading ? (
                  <TableRow><TableCell colSpan={9} className="py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
                ) : pendingBills.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="py-12 text-center text-muted-foreground">No pending bills — all customers are settled</TableCell></TableRow>
                ) : (
                  pendingBills.map((d, i) => (
                    <motion.tr key={d.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.3) }} className="border-b hover:bg-secondary/40" data-testid={`pending-bill-row-${d.id}`}>
                      <TableCell className="font-mono text-sm">{formatDate(d.date)}</TableCell>
                      <TableCell>
                        <div className="font-medium">{d.customer_name}</div>
                        <div className="font-mono text-xs text-muted-foreground">{d.customer_code}</div>
                      </TableCell>
                      <TableCell className="text-sm">{d.customer_area || "—"}</TableCell>
                      <TableCell className="max-w-[220px] truncate font-mono text-sm text-muted-foreground">{d.bills.map((b) => b.invoice_number).join(", ")}</TableCell>
                      <TableCell className="text-right font-mono">{formatINR(d.total_amount)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-600 dark:text-emerald-400">{formatINR(d.paid_amount)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-rose-600 dark:text-rose-400">{formatINR(d.outstanding)}</TableCell>
                      <TableCell className="text-right"><PayStatusBadge status={d.payment_status} /></TableCell>
                      <TableCell className="print-hidden text-right">
                        <Button variant="ghost" size="icon" title="Add payment" onClick={() => setPayFor(d)} data-testid={`pending-collect-${d.id}`}>
                          <Wallet className="h-4 w-4 text-primary" />
                        </Button>
                      </TableCell>
                    </motion.tr>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
        </div>
      )}

      {view === "history" && (
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/60">
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Area</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {user?.role === "admin" && <TableHead className="text-right">Edit</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
              ) : payments.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">No payments</TableCell></TableRow>
              ) : (
                payments.map((p, i) => (
                  <motion.tr key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.3) }} className="border-b hover:bg-secondary/40" data-testid={`payment-row-${p.id}`}>
                    <TableCell className="font-mono text-sm">{formatDate(p.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 font-medium">{p.customer_name}{p.spot && <SpotPayBadge />}</div>
                      <div className="font-mono text-xs text-muted-foreground">{p.customer_code}</div>
                    </TableCell>
                    <TableCell className="text-sm">{p.area || "—"}</TableCell>
                    <TableCell><PaymentBadge mode={p.mode} /></TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{refDisplay(p)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatINR(p.amount)}</TableCell>
                    {user?.role === "admin" && (
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`payment-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      </TableCell>
                    )}
                  </motion.tr>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      )}

      <PaymentCollectionModal delivery={payFor} open={!!payFor} onClose={() => setPayFor(null)} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="payment-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
            <DialogDescription>Correct a saved payment entry. The delivery balance recalculates automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Mode</Label>
              <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger data-testid="payment-edit-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Amount (₹)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-edit-amount" /></div>
            <div className="space-y-2"><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="payment-edit-ref" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Card Last 4</Label><Input maxLength={4} value={form.card_last4} onChange={(e) => setForm({ ...form, card_last4: e.target.value })} data-testid="payment-edit-card4" /></div>
              <div className="space-y-2"><Label>Bank</Label><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} data-testid="payment-edit-bank" /></div>
            </div>
            <div className="space-y-2"><Label>Remarks</Label><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="payment-edit-remarks" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={!form.amount || save.isPending} data-testid="payment-edit-save">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
